import {
  Check,
  CircleDollarSign,
  CirclePlus,
  LineChart,
  Rocket,
  UserRound,
} from 'lucide-react';
import { INVESTING_STYLE_OPTIONS } from '../data/mockOnboardingCatalog';
import type { InvestingStyleId } from '../types';

interface InvestingStyleCardsProps {
  selected: InvestingStyleId[];
  onToggle: (id: InvestingStyleId) => void;
}

function StyleIcon({ iconName }: { iconName: (typeof INVESTING_STYLE_OPTIONS)[number]['iconName'] }) {
  const className = 'h-3.5 w-3.5';

  switch (iconName) {
    case 'growth':
      return <Rocket className={className} aria-hidden="true" />;
    case 'value':
      return <CircleDollarSign className={className} aria-hidden="true" />;
    case 'compounder':
      return <LineChart className={className} aria-hidden="true" />;
    case 'dividend':
      return <CircleDollarSign className={className} aria-hidden="true" />;
    case 'founder':
      return <UserRound className={className} aria-hidden="true" />;
    default:
      return null;
  }
}

export default function InvestingStyleCards({ selected, onToggle }: InvestingStyleCardsProps) {
  return (
    <div className="flex flex-col gap-3 sm:gap-3" role="group" aria-label="Investing style">
      {INVESTING_STYLE_OPTIONS.map((option) => {
        const isSelected = selected.includes(option.id);
        return (
          <button
            key={option.id}
            type="button"
            role="checkbox"
            aria-checked={isSelected}
            aria-label={`${option.label}. ${option.description}`}
            onClick={() => onToggle(option.id)}
            className={`relative flex w-full items-center gap-2.5 rounded-lg border-2 px-3 py-2 text-left transition-colors duration-150 focus:outline-none ${
              isSelected
                ? 'border-gray-600 bg-white text-gray-900'
                : 'border-transparent bg-background text-gray-900 ring-1 ring-inset ring-gray-200 hover:ring-gray-300'
            }`}
          >
            <span
              className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${
                isSelected ? 'bg-gray-100 text-gray-700' : 'bg-gray-100 text-gray-500'
              }`}
            >
              <StyleIcon iconName={option.iconName} />
            </span>
            <span className="min-w-0 flex-1 pr-5">
              <span className="block text-sm font-medium leading-tight">{option.label}</span>
            </span>
            {isSelected ? (
              <span
                className="absolute right-2 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center rounded-full bg-emerald-600 text-white"
                aria-hidden="true"
              >
                <Check className="h-2.5 w-2.5" strokeWidth={3} />
              </span>
            ) : (
              <span
                className="absolute right-2 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-gray-400"
                aria-hidden="true"
              >
                <CirclePlus className="h-4 w-4" strokeWidth={2} />
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
